import React, { useState, useEffect } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import { formatMoney } from '../shared/formatMoney';
import OwnerNav from './OwnerNav';
import { HiDocumentText, HiDownload, HiExclamationCircle } from 'react-icons/hi';

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const generateInvoicePdf = (invoice, ownerCode) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const w = doc.internal.pageSize.getWidth();
    let y = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(249, 115, 22);
    doc.text('PAWAFLEET', 14, y);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    y += 6;
    doc.text('Monthly Owner Invoice', 14, y);

    y += 12;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30);
    doc.text(`Invoice #${invoice.id}`, 14, y);
    doc.text(`Date: ${fmt(invoice.date)}`, w - 14, y, { align: 'right' });

    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Owner: ${invoice.owner_name || ownerCode}`, 14, y);
    doc.text(`Code: ${invoice.owner_code || ownerCode}`, w - 14, y, { align: 'right' });

    y += 10;
    doc.setDrawColor(229, 231, 235);
    doc.line(14, y, w - 14, y);
    y += 6;

    if (invoice.description) {
        doc.setFontSize(9);
        doc.setTextColor(80);
        doc.text(invoice.description, 14, y);
        y += 8;
    }

    const details = invoice.details || [];
    if (details.length > 0) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(30);
        doc.text('Plate No', 14, y);
        doc.text('Description', 60, y);
        doc.text('Amount', w - 14, y, { align: 'right' });
        y += 2;
        doc.line(14, y, w - 14, y);
        y += 5;

        doc.setFont('helvetica', 'normal');
        details.forEach(d => {
            doc.text(d.plate_no || '', 14, y);
            doc.text(d.description || '', 60, y, { maxWidth: 80 });
            doc.text(formatMoney(d.amount), w - 14, y, { align: 'right' });
            y += 7;
        });
        doc.line(14, y, w - 14, y);
        y += 6;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(249, 115, 22);
    doc.text('Total:', w - 60, y);
    doc.text(formatMoney(invoice.amount), w - 14, y, { align: 'right' });

    doc.save(`invoice-${invoice.id}-${ownerCode}.pdf`);
};

const OwnerInvoices = () => {
    const { user } = useAuth();
    const ownerCode = user?.linked_entity_code;
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        if (!ownerCode) return;
        axios.get(`${rootUrl}/api/owners/${ownerCode}/invoices`)
            .then(res => setInvoices(res.data?.data || []))
            .catch(() => setError('Failed to load invoices.'))
            .finally(() => setLoading(false));
    }, [ownerCode]);

    const handleDownload = async (invoice) => {
        setDownloading(invoice.id);
        try {
            const res = await axios.get(`${rootUrl}/api/owners/${ownerCode}/invoices/${invoice.id}`);
            const detail = res.data?.data;
            if (detail) generateInvoicePdf(detail, ownerCode);
        } catch {
            setError('Failed to download invoice.');
        } finally {
            setDownloading(null);
        }
    };

    if (!ownerCode) return (
        <div className="min-h-screen flex items-center justify-center text-gray-500 pb-20">
            No owner account linked.
        </div>
    );

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center pb-20">
            <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="bg-orange-500 px-4 pt-10 pb-6">
                <h1 className="text-white text-xl font-bold">Invoices</h1>
            </div>

            <div className="px-4 py-4 space-y-3">
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2 text-red-600 text-sm">
                        <HiExclamationCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                    </div>
                )}

                {invoices.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
                        <HiDocumentText className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                        <p className="text-gray-400 text-sm">No invoices yet.</p>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                        <ul className="divide-y divide-gray-50">
                            {invoices.map(inv => (
                                <li key={inv.id} className="px-4 py-3 flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <HiDocumentText className="w-4 h-4 text-orange-400 flex-shrink-0" />
                                            <p className="text-sm font-medium text-gray-800">Invoice #{inv.id}</p>
                                        </div>
                                        <p className="text-xs text-gray-400 mt-0.5">{fmt(inv.date)}</p>
                                        {inv.car_count > 0 && (
                                            <p className="text-xs text-gray-500 mt-0.5">{inv.car_count} car{inv.car_count !== 1 ? 's' : ''}</p>
                                        )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                        <p className="text-sm font-semibold text-gray-800">{formatMoney(inv.amount)}</p>
                                        <button
                                            onClick={() => handleDownload(inv)}
                                            disabled={downloading === inv.id}
                                            className="flex items-center gap-1 text-xs text-orange-500 hover:text-orange-600 disabled:opacity-50"
                                        >
                                            <HiDownload className="w-3.5 h-3.5" />
                                            {downloading === inv.id ? 'Loading…' : 'PDF'}
                                        </button>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            <OwnerNav />
        </div>
    );
};

export default OwnerInvoices;
